/**
 * Agrupamento da prévia por família, para o passo 3 de nova despesa.
 *
 * A divisão em si é de `@nv/core/finance` e já está feita quando este arquivo
 * roda: aqui só se soma o que cada família ficou devendo, para que a tela
 * mostre a unidade que a modalidade usa. Somar por pessoa e dizer "por
 * família" é o que faria a tela mentir sobre o próprio modo escolhido.
 *
 * Quem não está em nenhuma família conta como uma unidade — é a regra de
 * `canSplitPerFamily`, e ela precisa estar visível na tela, não só no cálculo.
 */

import { formatMoney } from '@nv/core';

export interface MembroComFamilia {
  memberId: string;
  nome: string;
  familyId?: string | undefined;
  familyName?: string | undefined;
  familyColor?: string | undefined;
}

export interface CotaDeFamilia {
  /** `familyId`, ou o `memberId` de quem não tem família. */
  chave: string;
  nome: string;
  cor: string;
  /** Quantas pessoas da despesa estão nesta unidade. */
  membros: number;
  totalMinor: number;
  /** `true` quando a unidade é uma pessoa sem família. */
  avulso: boolean;
}

/** Cor de quem não tem família: neutra, para não parecer uma família a mais. */
const COR_AVULSA = '#8A8FA3';

export function cotasPorFamilia(
  shares: readonly { participantId: string; total: number }[],
  membros: readonly MembroComFamilia[],
): CotaDeFamilia[] {
  const porId = new Map(membros.map((m) => [m.memberId, m]));
  const cotas = new Map<string, CotaDeFamilia>();

  for (const share of shares) {
    const membro = porId.get(share.participantId);
    const familyId = membro?.familyId;
    const chave = familyId ?? share.participantId;

    const atual = cotas.get(chave);
    if (atual) {
      atual.membros += 1;
      atual.totalMinor += share.total;
      continue;
    }

    cotas.set(chave, {
      chave,
      nome: familyId
        ? (membro?.familyName ?? 'Família')
        : (membro?.nome ?? 'Participante'),
      cor: familyId ? (membro?.familyColor ?? COR_AVULSA) : COR_AVULSA,
      membros: 1,
      totalMinor: share.total,
      avulso: !familyId,
    });
  }

  // Famílias antes de avulsos, e cada bloco em ordem de nome: a mesma lista em
  // todo aparelho, e a leitura começa pelo que a modalidade agrupa.
  return [...cotas.values()].sort((a, b) => {
    if (a.avulso !== b.avulso) return a.avulso ? 1 : -1;
    return a.nome.localeCompare(b.nome, 'pt-BR');
  });
}

/**
 * A linha de resumo: "3 unidades · US$ 40,00 cada".
 *
 * Quando o total não é divisível em partes iguais, a diferença de centavos é
 * real (maior resto) e o texto passa a dizer "≈" em vez de afirmar um valor
 * exato que só vale para algumas das unidades.
 */
export function resumoDasCotas(cotas: readonly CotaDeFamilia[], currency: string): string {
  if (cotas.length === 0) return 'Nenhum participante.';

  const familias = cotas.filter((c) => !c.avulso).length;
  const avulsos = cotas.length - familias;

  const valores = cotas.map((c) => c.totalMinor);
  const iguais = valores.every((v) => v === valores[0]);
  const cada = formatMoney(valores[0] ?? 0, currency);

  const partes: string[] = [];
  if (familias > 0) partes.push(familias === 1 ? '1 família' : `${familias} famílias`);
  if (avulsos > 0) partes.push(avulsos === 1 ? '1 sem família' : `${avulsos} sem família`);

  return `${partes.join(' · ')} · ${iguais ? '' : '≈ '}${cada} cada`;
}
